import { produce } from 'immer';

import {
  ComponentSide,
  GeneratableBearing,
  HeelSettings,
  OverhangSettings,
  Point2D,
  Profile,
  ProfileDefaults,
  ProfileJoint,
  ProfileLine,
  ProfileLineType,
  ProfileTrussSettings,
} from '@paragon/contract-library';
import { degreesToSlope, slopeToDegrees } from '@paragon/geometry-library';
import { assertExhaustiveSwitch, circularPairwise } from '@paragon/utilities';

import {
  ConfigurableShapeDetails,
  FreeformProfileShapeDetails,
  JackShapeDetails,
  ScissorsShapeDetails,
} from '../../store/slices/applicationSlice';
import { getHeelHeightBasedOnSlope } from './profileManipulation';
import { getDefaultGableSettings, getDefaultHeelSettings, getDefaultOverhangSettings } from './profileTrussDefaults';

interface MaterialDependentOptions {
  readonly topChordWidth: number;
}

export function createProfileSettingsForShape(
  details: ConfigurableShapeDetails,
  defaults: ProfileDefaults,
  materialOptions: MaterialDependentOptions,
): ProfileTrussSettings {
  switch (details.type) {
    case 'Attic':
      return produce(createStandardSymmetricProfileSettings(details, defaults, materialOptions), (settings) => {
        settings.profile.atticSettings = details.atticSettings;
        settings.profile.topChordMaxPanelLength /= 2;
        settings.profile.bottomChordMaxPanelLength /= 2;
      });

    case 'Common':
      return createStandardSymmetricProfileSettings(details, defaults, materialOptions);

    case 'FreeformProfile':
      return createFreeformProfileSettings(details, defaults);

    case 'Gable': {
      const settings = createStandardSymmetricProfileSettings(details, defaults, materialOptions);
      return {
        ...settings,
        gableSettings: details.gableSettings,
        profile: { ...settings.profile, continuouslyBearing: true },
      };
    }

    case 'Jack':
      return createJackProfileSettings(details, defaults, materialOptions);

    case 'Scissors':
      return createScissorsProfileSettings(details, defaults, materialOptions);

    case 'Valley': {
      const settings = createProfileSettingsWithZeroHeelHeight(details, defaults);
      return {
        ...settings,
        profile: {
          ...settings.profile,
          leftHeelSettings: { ...settings.profile.leftHeelSettings, chordTypeRunThrough: 'Bottom' },
          rightHeelSettings: { ...settings.profile.leftHeelSettings, chordTypeRunThrough: 'Bottom' },
        },
      };
    }

    case 'Piggyback': {
      const settings = createProfileSettingsWithZeroHeelHeight(details, defaults);
      return {
        ...settings,
        profile: { ...settings.profile, piggybackSettings: details.piggybackSettings },
      };
    }

    default:
      assertExhaustiveSwitch(details);
  }
}

const defaultProfileProperties = {
  topChordMaxPanelLength: 8 * 12,
  bottomChordMaxPanelLength: 10 * 12,
  leftBevelCut: null,
  rightBevelCut: null,
  jackSettings: null,
  piggybackSettings: null,
  atticSettings: null,
  name: 'RT-',
  continuouslyBearing: false,
  spacing: 24,
  plies: 1,
} satisfies Partial<Profile>;

function getDefaultProfileSettingsProperties(defaults: ProfileDefaults) {
  return {
    definitionType: 'Profile',
    componentType: 'RoofTruss',
    name: 'RT-',
    gableSettings: getDefaultGableSettings(defaults),
    leftSeatCut: defaults.bearingWidth,
    rightSeatCut: defaults.bearingWidth,
  } as const;
}

interface StandardSymmetricShapeDetails {
  readonly span: number;
  readonly slopeInDegrees: number;
  readonly heelSettings: HeelSettings;
  readonly overhang: OverhangSettings;
}

function createStandardSymmetricProfileSettings(
  { span, slopeInDegrees, heelSettings, overhang }: StandardSymmetricShapeDetails,
  defaults: ProfileDefaults,
  { topChordWidth }: MaterialDependentOptions,
): ProfileTrussSettings {
  const slopeOverTwelve = degreesToSlope(slopeInDegrees);
  const heelHeight = getHeelHeightBasedOnSlope(heelSettings, slopeOverTwelve, topChordWidth);

  const topPointY = (slopeOverTwelve / 12) * (span / 2);
  const xCoordinates = [0, span / 2, span];
  const yCoordinates = [0, heelHeight, topPointY + heelHeight];
  const { profileJoints, profileLines } = createJointsAndLines([
    { point: { x: xCoordinates[0], y: yCoordinates[0] }, lineType: 'Heel' },
    { point: { x: xCoordinates[0], y: yCoordinates[1] }, lineType: 'TopChord' },
    { point: { x: xCoordinates[1], y: yCoordinates[2] }, lineType: 'TopChord' },
    { point: { x: xCoordinates[2], y: yCoordinates[1] }, lineType: 'Heel' },
    { point: { x: xCoordinates[2], y: yCoordinates[0] }, lineType: 'BottomChord' },
  ]);

  const profile: Profile = {
    ...defaultProfileProperties,
    profileJoints,
    profileLines,
    guid: crypto.randomUUID(),
    leftOverhang: overhang,
    rightOverhang: overhang,
    leftHeelSettings: heelSettings,
    rightHeelSettings: heelSettings,
    generatableBearings: createDefaultBearings(defaults),
  };

  return { ...getDefaultProfileSettingsProperties(defaults), profile, span };
}

function createFreeformProfileSettings(details: FreeformProfileShapeDetails, defaults: ProfileDefaults) {
  const profile: Profile = {
    ...defaultProfileProperties,

    profileJoints: [],
    profileLines: [],
    guid: crypto.randomUUID(),

    leftOverhang: getDefaultOverhangSettings(defaults),
    rightOverhang: getDefaultOverhangSettings(defaults),
    leftHeelSettings: getDefaultHeelSettings(defaults),
    rightHeelSettings: getDefaultHeelSettings(defaults),

    generatableBearings: createDefaultBearings(defaults),
  };

  return { ...getDefaultProfileSettingsProperties(defaults), profile, span: details.span };
}

function createJackProfileSettings(
  { span, slopeInDegrees, leftHeelSettings, leftOverhang }: JackShapeDetails,
  defaults: ProfileDefaults,
  { topChordWidth }: MaterialDependentOptions,
): ProfileTrussSettings {
  const slopeOverTwelve = degreesToSlope(slopeInDegrees);
  const heelHeight = getHeelHeightBasedOnSlope(leftHeelSettings, slopeOverTwelve, topChordWidth);

  const topPointY = (slopeOverTwelve / 12) * span;
  const xCoordinates = [0, span];
  const yCoordinates = [0, heelHeight, topPointY + heelHeight];
  const { profileJoints, profileLines } = createJointsAndLines([
    { point: { x: xCoordinates[0], y: yCoordinates[0] }, lineType: 'Heel' },
    { point: { x: xCoordinates[0], y: yCoordinates[1] }, lineType: 'TopChord' },
    { point: { x: xCoordinates[1], y: yCoordinates[2] }, lineType: 'Heel' },
    { point: { x: xCoordinates[1], y: yCoordinates[0] }, lineType: 'BottomChord' },
  ]);

  const bearings: readonly GeneratableBearing[] = [
    createDefaultBearing('Left', defaults),
    { ...createDefaultBearing('Right', defaults), bearingType: 'Hanger' },
  ];

  const profile: Profile = {
    ...defaultProfileProperties,
    profileJoints,
    profileLines,
    guid: crypto.randomUUID(),
    leftOverhang,
    rightOverhang: zeroOverhang,
    leftHeelSettings: getDefaultHeelSettings(defaults),
    rightHeelSettings: createHeelSettingsForHeelHeight(topPointY + heelHeight),
    rightBevelCut: { cutType: 'Double', frontAngle: 45, backAngle: 45 },
    generatableBearings: bearings,
  };

  return { ...getDefaultProfileSettingsProperties(defaults), profile, span };
}

function createScissorsProfileSettings(
  { span, topChordSlopeInDegrees, bottomChordSlopeInDegrees, heelSettings, overhang, seatCut }: ScissorsShapeDetails,
  defaults: ProfileDefaults,
  { topChordWidth }: MaterialDependentOptions,
): ProfileTrussSettings {
  const topChordSlopeOverTwelve = degreesToSlope(topChordSlopeInDegrees);
  const bottomChordSlopeOverTwelve = degreesToSlope(bottomChordSlopeInDegrees);
  const heelHeight = getHeelHeightBasedOnSlope(heelSettings, topChordSlopeOverTwelve, topChordWidth);

  const topPointY = (topChordSlopeOverTwelve / 12) * (span / 2);
  const bottomChordTopPointY = (bottomChordSlopeOverTwelve / 12) * (span / 2 - seatCut);
  const xCoordinates = [0, seatCut, span / 2, span - seatCut, span];
  const yCoordinates = [0, heelHeight, bottomChordTopPointY, topPointY + heelHeight];
  const { profileJoints, profileLines } = createJointsAndLines([
    { point: { x: xCoordinates[0], y: yCoordinates[0] }, lineType: 'Heel' },
    { point: { x: xCoordinates[0], y: yCoordinates[1] }, lineType: 'TopChord' },
    { point: { x: xCoordinates[2], y: yCoordinates[3] }, lineType: 'TopChord' },
    { point: { x: xCoordinates[4], y: yCoordinates[1] }, lineType: 'Heel' },
    { point: { x: xCoordinates[4], y: yCoordinates[0] }, lineType: 'SeatCut' },
    { point: { x: xCoordinates[3], y: yCoordinates[0] }, lineType: 'BottomChord' },
    { point: { x: xCoordinates[2], y: yCoordinates[2] }, lineType: 'BottomChord' },
    { point: { x: xCoordinates[1], y: yCoordinates[0] }, lineType: 'SeatCut' },
  ]);

  const profile: Profile = {
    ...defaultProfileProperties,
    profileJoints,
    profileLines,
    guid: crypto.randomUUID(),
    leftOverhang: overhang,
    rightOverhang: overhang,
    leftHeelSettings: heelSettings,
    rightHeelSettings: heelSettings,
    generatableBearings: createDefaultBearings(defaults),
  };

  return {
    ...getDefaultProfileSettingsProperties(defaults),
    profile,
    span,
    leftSeatCut: seatCut,
    rightSeatCut: seatCut,
  };
}

interface ZeroHeelHeightShapeDetails {
  readonly span: number;
  readonly slopeInDegrees: number;
}

function createProfileSettingsWithZeroHeelHeight(
  { span, slopeInDegrees }: ZeroHeelHeightShapeDetails,
  defaults: ProfileDefaults,
): ProfileTrussSettings {
  const slopeOverTwelve = degreesToSlope(slopeInDegrees);

  const topPointY = (slopeOverTwelve / 12) * (span / 2);
  const xCoordinates = [0, span / 2, span];
  const yCoordinates = [0, topPointY];
  const { profileJoints, profileLines } = createJointsAndLines([
    { point: { x: xCoordinates[0], y: yCoordinates[0] }, lineType: 'TopChord' },
    { point: { x: xCoordinates[1], y: yCoordinates[1] }, lineType: 'TopChord' },
    { point: { x: xCoordinates[2], y: yCoordinates[0] }, lineType: 'BottomChord' },
  ]);

  const profile: Profile = {
    ...defaultProfileProperties,
    profileJoints,
    profileLines,
    guid: crypto.randomUUID(),
    leftOverhang: zeroOverhang,
    rightOverhang: zeroOverhang,
    leftHeelSettings: zeroHeightHeelSettings,
    rightHeelSettings: zeroHeightHeelSettings,
    generatableBearings: [],
    continuouslyBearing: true,
  };

  return { ...getDefaultProfileSettingsProperties(defaults), profile, span };
}

function createProfileLine(
  profileLineType: ProfileLineType,
  baseJoint: ProfileJoint,
  endJoint: ProfileJoint,
): ProfileLine {
  return {
    guid: crypto.randomUUID(),
    profileLineType,
    fixedJoints: [],
    featureJoints: [],
    webJointSegments: [
      {
        guid: crypto.randomUUID(),
        baseJointGuid: baseJoint.guid,
        endJointGuid: endJoint.guid,
        numberOfFloatingJoints: null,
        floatingJoints: [],
      },
    ],
    baseJointGuid: baseJoint.guid,
    endJointGuid: endJoint.guid,
  };
}

interface PointAndLineType {
  readonly point: Point2D;
  readonly lineType: ProfileLineType;
}

function createJointsAndLines(pointsAndLineTypes: readonly PointAndLineType[]) {
  const profileJoints = pointsAndLineTypes.map(({ point }): ProfileJoint => ({
    guid: crypto.randomUUID(),
    location: point,
  }));
  const profileLines = circularPairwise(profileJoints).map(([baseJoint, endJoint], index) =>
    createProfileLine(pointsAndLineTypes[index].lineType, baseJoint, endJoint),
  );
  return { profileJoints, profileLines };
}

export function createDefaultProfileTrussSettings(
  defaults: ProfileDefaults,
  materialOptions: MaterialDependentOptions,
): ProfileTrussSettings {
  return createStandardSymmetricProfileSettings(
    {
      span: 24 * 12,
      slopeInDegrees: slopeToDegrees(6),
      heelSettings: getDefaultHeelSettings(defaults),
      overhang: getDefaultOverhangSettings(defaults),
    },
    defaults,
    materialOptions,
  );
}

const zeroOverhang: OverhangSettings = { overhangCutType: 'Plumb', distance: 0 };

const zeroHeightHeelSettings: HeelSettings = createHeelSettingsForHeelHeight(0);

function createHeelSettingsForHeelHeight(heelHeight: number): HeelSettings {
  return {
    heelMethod: 'HeelHeight',
    interiorButtCut: 0.25,
    chordTypeRunThrough: 'Top',
    heelHeight,
  };
}

function createDefaultBearings(defaults: ProfileDefaults) {
  return [createDefaultBearing('Left', defaults), createDefaultBearing('Right', defaults)];
}

function createDefaultBearing(side: ComponentSide, defaults: ProfileDefaults): GeneratableBearing {
  return {
    generatableBearingType: 'HorizontalGeneratableBearing',
    guid: crypto.randomUUID(),
    offset: 0,
    width: defaults.bearingWidth,
    chordLocation: 'Bottom',
    bearingType: 'DoublePlate',
    offsetSide: side,
    connectors: [],
    anchor: side,
    isContinuous: false,
  };
}
